import React, { useEffect, useState, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { adminCarrotClient } from "../../../utils/CarrotClient"

const MedicalInfoContext = React.createContext(null)
export const useMedicalInfo = () => React.useContext(MedicalInfoContext)

export function MedicalInfoContextProvider({ children }): JSX.Element {
  const dedupeRef = useRef(false)
  const { isLoading, error, data, refetch } = useQuery(
    ["carrotPlansMedicalInfo"],
    () => adminCarrotClient.getCarrotPlansMedicalInfo(),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false
    }
  )

  const [initialLoading, setInitialLoading] = useState(isLoading)

  useEffect(() => {
    if (initialLoading) {
      setInitialLoading(isLoading)
    }
  }, [isLoading, initialLoading])

  async function startMedicalInfo(): Promise<void> {
    if (!dedupeRef.current) {
      dedupeRef.current = true
      await adminCarrotClient.startMedicalInfoFlow().catch(() => (dedupeRef.current = false))
      refetch({ throwOnError: true })
      dedupeRef.current = false
    }
  }

  async function updateMedicalInfo(payload, retry = true): Promise<void> {
    await adminCarrotClient.updateMedicalInfo(payload).catch(async (error) => {
      if (retry) {
        await startMedicalInfo()
        const catchUp = data || {}
        await updateMedicalInfo({ ...catchUp, ...payload }, false)
      } else {
        throw error
      }
    })
    await refetch({ throwOnError: true })
  }

  function hasQuestionBeenSkipped(questionKey: string): boolean {
    return (data?.skipFields || []).includes(questionKey)
  }

  return (
    <MedicalInfoContext.Provider
      value={{
        error,
        isLoading,
        initialLoading,
        medicalInfo: data,
        updateMedicalInfo,
        startMedicalInfo,
        hasQuestionBeenSkipped,
        refetch
      }}
    >
      {children}
    </MedicalInfoContext.Provider>
  )
}
